import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Link } from "react-router-dom";

/**
 * UsagePanel — renders the authenticated user's current-cycle allowances and
 * remaining amounts across all metered resources (Critical Task 4).
 *
 * Silent-fails on 402 / 403 (user isn't past the access-control gate yet) so
 * the panel simply hides itself on those states. Uses backend-provided labels
 * (`label_en` / `label_ar`) so translation stays server-driven.
 */
export function UsagePanel({ compact = false }) {
  const { lang } = useI18n();
  const [usage, setUsage] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | hidden

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/usage");
        if (cancelled) return;
        setUsage(data);
        setState("ready");
      } catch (e) {
        // 402/403 → hidden (gate handled elsewhere). Other errors → hidden.
        if (!cancelled) setState("hidden");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state !== "ready" || !usage) return null;

  const cycleLabel = lang === "ar"
    ? "الدورة الحالية"
    : "Current cycle";
  const remainingLabel = lang === "ar" ? "المتبقي" : "Remaining";
  const usedLabel = lang === "ar" ? "المستخدم" : "Used";
  const resetLabel = lang === "ar" ? "يتجدد في" : "Resets";
  const cycleEnd = usage.cycle_end ? new Date(usage.cycle_end).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US") : "";

  return (
    <div data-testid="usage-panel" className="nx-card p-5 mb-6">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="font-display text-lg">
            {lang === "ar" ? "استخدامك الشهري" : "Your monthly usage"}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {cycleLabel} · {resetLabel} {cycleEnd}
          </p>
        </div>
        <Link
          to="/app/plans"
          data-testid="usage-upgrade-link"
          className="text-xs underline text-muted-foreground hover:text-foreground"
        >
          {lang === "ar" ? "ترقية الخطة" : "Upgrade plan"}
        </Link>
      </div>

      <div className={`grid gap-3 ${compact ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-5"}`}>
        {Object.entries(usage.resources || {}).map(([key, r]) => {
          const label = lang === "ar" ? r.label_ar : r.label_en;
          const pct = r.limit ? Math.min(100, Math.round((r.used / r.limit) * 100)) : 0;
          const critical = pct >= 90;
          const warn = pct >= 75 && pct < 90;
          return (
            <div key={key} data-testid={`usage-row-${key}`} className="rounded-sm border border-border p-3">
              <div className="text-xs text-muted-foreground truncate" title={label}>{label}</div>
              <div className="mt-1 text-sm font-medium tabular-nums">
                {r.used} <span className="text-muted-foreground">/ {r.limit ?? "∞"}</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className={
                    "h-full transition-all " +
                    (critical ? "bg-red-500" : warn ? "bg-amber-500" : "bg-emerald-500")
                  }
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground tabular-nums">
                {remainingLabel}: {r.remaining ?? "∞"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default UsagePanel;
